import React from 'react';
import { connect } from 'react-redux';
import { filterDviewCameras, filterEviewGroups } from '../util/cameraCatalog';
import { getProductMode } from '../backend/productMode';
import './cameraSelectStyle.css';

/**
 * CameraSelectDialog - Modal dialog for selecting a camera to assign to a slot
 *
 * In dview mode: Shows cameras 1-N based on bNumCams from current NVR
 * In eView mode: Shows cameras from ALL connected NVRs, grouped by server
 *
 * Mirrors Java dview's SlotChangeDialog behavior for camera selection
 */
export class CameraSelectDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hoveredCamera: null,  // compositeKey in eView, bID in dview
      expandedServers: {},  // Track which server sections are expanded
      search: '',           // raw search box value (controlled input)
      debouncedSearch: ''   // 200ms-debounced query used for filtering (Java CameraTreePanel:99)
    };
    this._searchTimer = null;
  }

  componentWillUnmount() {
    if (this._searchTimer) clearTimeout(this._searchTimer);
  }

  // Type-ahead filter (#86bb5dx62 L-C3): debounced 200ms like Java's edit-tree filter.
  handleSearchChange = (e) => {
    const value = e.target.value;
    this.setState({ search: value });
    if (this._searchTimer) clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this.setState({ debouncedSearch: value.trim().toLowerCase() });
    }, 200);
  };

  clearSearch = () => {
    if (this._searchTimer) clearTimeout(this._searchTimer);
    this.setState({ search: '', debouncedSearch: '' });
  };

  handleCameraSelect = (camera) => {
    if (this.props.onSelectCamera) {
      this.props.onSelectCamera(camera);
    }
  };

  handleClose = () => {
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      this.handleClose();
    }
  };

  toggleServerExpand = (bSerial) => {
    this.setState(prev => ({
      expandedServers: {
        ...prev.expandedServers,
        [bSerial]: !prev.expandedServers[bSerial]
      }
    }));
  };

  /**
   * Check if user has access to a camera
   * In dview mode: check userCameras array
   * In eView mode: TODO - check per-server access rights
   */
  checkCameraAccess = (camera) => {
    const { userCameras, useEviewLogin } = this.props;

    if (useEviewLogin) {
      // In eView mode, assume access for now (TODO: per-server access checking)
      return true;
    }

    // dview mode - check local userCameras
    return userCameras.indexOf(camera.bID) > -1;
  };

  /**
   * Render camera list for dview mode (single NVR)
   */
  renderDviewCameras() {
    const { userCameras } = this.props;

    // Available cameras (1..bNumCams, enabled, matching the search) — shared enumeration.
    const availableCameras = filterDviewCameras(this.props, this.state.debouncedSearch);

    if (availableCameras.length === 0) {
      return (
        <div style={styles.emptyMessage}>
          {this.state.debouncedSearch ? `No cameras match "${this.state.search.trim()}"` : 'No cameras available'}
        </div>
      );
    }

    return availableCameras.map((camera) => {
      const isHovered = this.state.hoveredCamera === camera.bID;
      const hasAccess = userCameras.indexOf(camera.bID) > -1;

      return (
        <div
          key={camera.bID}
          style={{
            ...styles.cameraItem,
            backgroundColor: isHovered ? '#3d3d3d' : 'transparent',
            opacity: hasAccess ? 1 : 0.5,
            cursor: hasAccess ? 'pointer' : 'not-allowed'
          }}
          onClick={() => hasAccess && this.handleCameraSelect(camera)}
          onMouseEnter={() => this.setState({ hoveredCamera: camera.bID })}
          onMouseLeave={() => this.setState({ hoveredCamera: null })}
        >
          <span style={styles.cameraNumber}>{camera.bID}</span>
          <span style={styles.cameraName}>{camera.sName}</span>
          {!hasAccess && (
            <span style={styles.noAccessBadge}>No Access</span>
          )}
        </div>
      );
    });
  }

  /**
   * Render camera list for eView mode (multi-NVR)
   * Cameras grouped by server with section headers
   * Format mirrors Java Slot.getFullName(): "ServerName : CamID - CamName"
   */
  renderEviewCameras() {
    const { allCameras } = this.props;

    if (!allCameras || allCameras.length === 0) {
      return <div style={styles.emptyMessage}>No cameras available</div>;
    }

    const searching = !!this.state.debouncedSearch;

    // Servers (with their enabled+matching cameras) — shared enumeration.
    const groups = filterEviewGroups(this.props, this.state.debouncedSearch);
    const serversWithCameras = groups.map(g => g.server);

    if (serversWithCameras.length === 0) {
      return (
        <div style={styles.emptyMessage}>
          {searching ? `No cameras match "${this.state.search.trim()}"` : 'No cameras available'}
        </div>
      );
    }

    // By default, all servers are expanded; while searching, force every group open.
    const elements = [];

    groups.forEach(({ server, cameras: enabledCameras }, serverIndex) => {
      const isExpanded = searching || this.state.expandedServers[server.bSerial] !== false; // Default to expanded

      // Server header
      elements.push(
        <div
          key={`server_${server.bSerial}`}
          style={styles.serverHeader}
          onClick={() => this.toggleServerExpand(server.bSerial)}
        >
          <span style={styles.serverExpandIcon}>{isExpanded ? '▼' : '▶'}</span>
          <span style={styles.serverName}>{server.sName}</span>
          <span style={styles.serverCamCount}>({enabledCameras.length} cameras)</span>
        </div>
      );

      // Cameras under this server (if expanded)
      if (isExpanded) {
        enabledCameras.forEach((camera) => {
          const cameraKey = camera.compositeKey || `${camera.bSerial}_${camera.bID}`;
          const isHovered = this.state.hoveredCamera === cameraKey;
          const hasAccess = this.checkCameraAccess(camera);

          elements.push(
            <div
              key={cameraKey}
              style={{
                ...styles.cameraItem,
                ...styles.eviewCameraItem,
                backgroundColor: isHovered ? '#3d3d3d' : 'transparent',
                opacity: hasAccess ? 1 : 0.5,
                cursor: hasAccess ? 'pointer' : 'not-allowed'
              }}
              onClick={() => hasAccess && this.handleCameraSelect(camera)}
              onMouseEnter={() => this.setState({ hoveredCamera: cameraKey })}
              onMouseLeave={() => this.setState({ hoveredCamera: null })}
            >
              <span style={styles.cameraNumber}>{String(camera.bID).padStart(2, '0')}</span>
              <span style={styles.cameraName}>{camera.sName}</span>
              {!hasAccess && (
                <span style={styles.noAccessBadge}>No Access</span>
              )}
            </div>
          );
        });
      }

      // Add separator between servers (except after last)
      if (serverIndex < serversWithCameras.length - 1) {
        elements.push(
          <div key={`sep_${server.bSerial}`} style={styles.serverSeparator} />
        );
      }
    });

    return elements;
  }

  render() {
    const { visible, slotIndex, useEviewLogin, useCloudLogin } = this.props;

    if (!visible) return null;

    const slotNum = slotIndex + 1;

    return (
      // MOBILE-FIRST: a bottom sheet on mobile+tablet, a centered modal on desktop (cameraSelectStyle.css).
      <div className="camsel-scrim" onClick={this.handleOverlayClick}>
        <div className="camsel-sheet" role="dialog" aria-label="Select camera">
          <div className="camsel-sheet__grip" aria-hidden="true" />
          <div style={styles.header}>
            <span style={styles.title}>
              {useEviewLogin ? 'Select Camera' : `Assign Camera to Slot ${slotNum}`}
            </span>
            <button style={styles.closeButton} onClick={this.handleClose}>
              &times;
            </button>
          </div>

          {useEviewLogin && (
            <div style={styles.modeIndicator}>
              {useCloudLogin ? 'Select a camera from any of your devices' : 'Enterprise Viewer - Select from any connected NVR'}
            </div>
          )}

          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 10-.7.7l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0A4.5 4.5 0 1114 9.5 4.5 4.5 0 019.5 14z"/></svg>
            </span>
            <input
              type="text"
              style={styles.searchInput}
              placeholder="Search cameras…"
              value={this.state.search}
              onChange={this.handleSearchChange}
              autoFocus
            />
            {this.state.search && (
              <button style={styles.searchClear} onClick={this.clearSearch} aria-label="Clear search">&times;</button>
            )}
          </div>

          <div style={styles.cameraList}>
            {useEviewLogin ? this.renderEviewCameras() : this.renderDviewCameras()}
          </div>

          <div style={styles.footer}>
            <button style={styles.cancelButton} onClick={this.handleClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }
}

// The scrim + sheet container now live in cameraSelectStyle.css (.camsel-scrim / .camsel-sheet);
// the former inline `overlay` / `dialog` style objects were dead after that move and are removed.
const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #444',
  },
  title: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#999',
    fontSize: 24,
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1,
  },
  modeIndicator: {
    backgroundColor: '#06704c', /* --brand-eview */
    color: '#8f8',
    fontSize: 12,
    padding: '8px 20px',
    textAlign: 'center',
  },
  searchWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    borderBottom: '1px solid #444',
  },
  searchIcon: {
    position: 'absolute',
    left: 26,
    color: '#888',
    display: 'flex',
    pointerEvents: 'none',
  },
  searchInput: {
    flex: 1,
    background: '#1e1e1e',
    border: '1px solid #555',
    borderRadius: 6,
    color: '#fff',
    fontSize: 14,
    padding: '9px 30px 9px 34px',
    outline: 'none',
    width: '100%',
  },
  searchClear: {
    position: 'absolute',
    right: 24,
    background: 'none',
    border: 'none',
    color: '#999',
    fontSize: 20,
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 4px',
  },
  cameraList: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 0',
    minHeight: 0, // flex child must be able to shrink so it scrolls within the sheet/modal (not a fixed max)
  },
  // Server section header for eView mode
  serverHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 20px',
    backgroundColor: '#383838',
    cursor: 'pointer',
    userSelect: 'none',
  },
  serverExpandIcon: {
    color: '#888',
    fontSize: 10,
    marginRight: 8,
    width: 12,
  },
  serverName: {
    color: '#0af',
    fontSize: 14,
    fontWeight: 600,
    flex: 1,
  },
  serverCamCount: {
    color: '#888',
    fontSize: 12,
  },
  serverSeparator: {
    height: 1,
    backgroundColor: '#444',
    margin: '4px 0',
  },
  // Camera item styles
  cameraItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 20px',
    transition: 'background-color 0.15s ease',
  },
  eviewCameraItem: {
    paddingLeft: 40,  // Indent under server header
  },
  cameraNumber: {
    color: '#0af',
    fontSize: 14,
    fontWeight: 600,
    width: 32,
    textAlign: 'right',
    marginRight: 12,
  },
  cameraName: {
    color: '#fff',
    fontSize: 14,
    flex: 1,
  },
  noAccessBadge: {
    color: '#f55',
    fontSize: 11,
    padding: '2px 6px',
    backgroundColor: 'rgba(255, 85, 85, 0.2)',
    borderRadius: 3,
    marginLeft: 8,
  },
  emptyMessage: {
    color: '#999',
    fontSize: 14,
    textAlign: 'center',
    padding: '40px 20px',
  },
  footer: {
    padding: '12px 20px',
    borderTop: '1px solid #444',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    backgroundColor: '#444',
    border: 'none',
    borderRadius: 4,
    color: '#fff',
    fontSize: 14,
    padding: '8px 16px',
    cursor: 'pointer',
  },
};

export const mapStateToProps = (state) => {
  const { cameras, bNumCams, allCameras, camerasByServer, authServers } = state.server;
  const { userCameras } = state.user;
  const { originalCameras } = state.view;
  const { useEviewLogin } = state.auth;

  // CLOUD is inherently multi-device, so the assign dialog REUSES the same grouped, all-devices render as
  // eView (card 86bbqvr65). "cloud" is derived from PRODUCT MODE (the single source of truth) — NOT a
  // separate persisted flag, and NOT by flipping global state.auth.useEviewLogin (that would trip
  // local-eView session/auth paths in cloud). We fold it into the grouped-render prop HERE only, so eView
  // and local-dview render exactly as before. The pool is already filtered server-side to the user's
  // granted cameras (buildLoginSession), so every camera shown is accessible.
  const useCloudLogin = getProductMode(state) === 'cloud';
  const grouped = !!useEviewLogin || useCloudLogin;
  // Cloud never dispatches SET_AUTH_SERVERS (a local-eView action); derive the server list from the
  // already-grouped camerasByServer (the SET_ALL_EVIEW_CAMERAS reducer built it from the cloud pool, each
  // camera carrying bSerial + sServerName). eView is untouched — its real authServers win.
  const cloudAuthServers = Object.keys(camerasByServer || {}).map((bSerial) => ({
    bSerial: Number(bSerial),
    sName: (camerasByServer[bSerial] && camerasByServer[bSerial][0] && camerasByServer[bSerial][0].sServerName) || 'Device',
  }));

  return {
    cameras,
    originalCameras,
    bNumCams: bNumCams || 64,
    userCameras: userCameras || [],
    // Grouped multi-device render shared by eView AND cloud (see above); local dview stays flat.
    useEviewLogin: grouped,
    // Distinguishes cloud from eView so shared-render copy (the mode banner) reads product-appropriate
    // instead of leaking "Enterprise Viewer" into cloud.
    useCloudLogin,
    allCameras: allCameras || [],
    camerasByServer: camerasByServer || {},
    authServers: (useCloudLogin && (!authServers || authServers.length === 0)) ? cloudAuthServers : (authServers || []),
  };
};

export default connect(mapStateToProps)(CameraSelectDialog);
